import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { mockLeads } from '@/mocks/leads';
import { mockPartners } from '@/mocks/partners';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import { mockOffers } from '@/mocks/offers';
import { mockAuditLog } from '@/mocks/audit';
import BaseSelect from '@/components/base/BaseSelect';
import {
  ArrowLeft, Mail, Phone, Globe, MapPin, Shield, AlertTriangle,
  Copy, Check, Save, CheckCircle,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

function AiIndicator({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--accent)' }} />
      {children}
    </span>
  );
}

function renderIntakeData(data: Record<string, unknown>) {
  const priorityFields = ['floor_type', 'surface_area_m2', 'rooms', 'budget_range', 'timeline_urgency', 'service_type', 'service_subtype', 'floor_subtype'];
  const entries = Object.entries(data).sort((a, b) => {
    const ia = priorityFields.indexOf(a[0]);
    const ib = priorityFields.indexOf(b[0]);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return 0;
  });

  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => {
        const label = key.replace(/_/g, ' ');
        return (
          <div key={key}>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>{label}</label>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {Array.isArray(value) ? (
                <div className="flex flex-wrap gap-1.5">
                  {value.map((v, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-md border" style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}>{String(v)}</span>
                  ))}
                </div>
              ) : typeof value === 'boolean' ? (
                <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: value ? 'var(--success-text)' : 'var(--text-muted)' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: value ? 'var(--accent)' : 'var(--text-muted)' }} />
                  {value ? 'Yes' : 'No'}
                </span>
              ) : key === 'surface_area_m2' ? (
                <span className="font-medium">{value} m²</span>
              ) : (
                <span>{value === null || value === undefined ? '—' : String(value)}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('');
  const [operatorNotes, setOperatorNotes] = useState('');
  const [statusSaved, setStatusSaved] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [validating, setValidating] = useState(false);

  const lead = useMemo(() => mockLeads.find(l => l.id === id), [id]);

  if (!lead) {
    return (
      <div className="text-center py-16">
        <p className="text-2xl font-bold" style={{ color: 'var(--text-muted)' }}>Lead not found.</p>
        <button
          onClick={() => navigate('/leads')}
          className="mt-4 text-sm transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          Back to leads
        </button>
      </div>
    );
  }

  const vertical = mockVerticals.find(v => v.id === lead.vertical_id);
  const geo = mockGeos.find(g => g.id === lead.geo_id);
  const partner = mockPartners.find(p => p.id === lead.assigned_partner_id);
  const offer = mockOffers.find(o => o.id === lead.assigned_offer_id);
  const audit = mockAuditLog
    .filter(a => a.entity_id === lead.id)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const copyToken = () => {
    navigator.clipboard.writeText(lead.lead_token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStatusSave = () => {
    if (!status || status === lead.status) return;
    setStatusSaved(true);
    setTimeout(() => setStatusSaved(false), 2000);
  };

  const handleSaveNotes = () => {
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  const handleValidate = async () => {
    setValidating(true);
    await new Promise(r => setTimeout(r, 800));
    setValidating(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/leads')}
          className="p-2 rounded-lg transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            {lead.firstname} {lead.lastname}
          </h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-md border"
              style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
            >
              {vertical?.name}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{geo?.name}</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>

      {/* Fraud banner */}
      {lead.fraud_signals.length > 0 && (
        <div className="flex items-start gap-3 p-5 rounded-xl border" style={{ background: 'var(--danger-bg)', borderColor: 'var(--danger-border)' }}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--danger-text)' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--danger-text)' }}>Fraud signals detected</p>
            <ul className="mt-1.5 space-y-1">
              {lead.fraud_signals.map((sig, i) => (
                <li key={i} className="text-xs" style={{ color: 'var(--danger-text)' }}>{sig}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Three-column card feed */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left — Identity (35%) */}
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Identity</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Full Name</label>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{lead.firstname} {lead.lastname}</p>
              </div>
              {lead.email && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Email</label>
                  <a href={`mailto:${lead.email}`} className="text-sm flex items-center gap-1.5 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                    <Mail size={12} style={{ color: 'var(--text-muted)' }} />
                    {lead.email}
                  </a>
                </div>
              )}
              {lead.phone && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Phone</label>
                  <a href={`tel:${lead.phone}`} className="text-sm flex items-center gap-1.5 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                    <Phone size={12} style={{ color: 'var(--text-muted)' }} />
                    {lead.phone}
                  </a>
                </div>
              )}
              <div className="flex items-start gap-2">
                <MapPin size={12} className="mt-0.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{lead.postcode}, {lead.city}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{lead.country_code}</p>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Lead Token</label>
                <div className="flex items-center gap-2">
                  <code className="text-xs px-3 py-1.5 rounded-lg font-mono border" style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}>
                    {lead.lead_token}
                  </code>
                  <button onClick={copyToken} className="p-1.5 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }}>
                    {copied ? <Check size={12} style={{ color: 'var(--accent)' }} /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quality */}
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Quality Score</h3>
            <div className="flex items-center gap-4">
              <AiIndicator>
                <span className="text-3xl font-bold" style={{
                  color: (lead.quality_score || 0) >= 0.7 ? 'var(--text-primary)' :
                    (lead.quality_score || 0) >= 0.4 ? 'var(--warning-text)' : 'var(--danger-text)',
                }}>
                  {lead.quality_score?.toFixed(2) || '—'}
                </span>
              </AiIndicator>
              <div className="flex-1">
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                  <div className="h-full rounded-full" style={{
                    width: `${((lead.quality_score || 0) * 100)}%`,
                    background: (lead.quality_score || 0) >= 0.7 ? 'var(--accent)' :
                      (lead.quality_score || 0) >= 0.4 ? 'var(--warning-dot)' : 'var(--danger-dot)',
                  }} />
                </div>
              </div>
            </div>
            {lead.quality_flags.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Quality Flags</p>
                <div className="flex flex-wrap gap-1.5">
                  {lead.quality_flags.map((flag, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-full border" style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)', borderColor: 'var(--warning-border)' }}>
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Attribution */}
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Attribution</h3>
            <div className="space-y-3 text-sm">
              {lead.source_url && (
                <div className="flex items-start gap-2">
                  <Globe size={12} className="mt-0.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
                  <a href={lead.source_url} target="_blank" rel="noopener noreferrer" className="truncate max-w-full transition-colors" style={{ color: 'var(--text-tertiary)' }}>
                    {lead.source_url}
                  </a>
                </div>
              )}
              {lead.utm_source && <p><span style={{ color: 'var(--text-muted)' }}>Source:</span> <span style={{ color: 'var(--text-secondary)' }}>{lead.utm_source}</span></p>}
              {lead.utm_medium && <p><span style={{ color: 'var(--text-muted)' }}>Medium:</span> <span style={{ color: 'var(--text-secondary)' }}>{lead.utm_medium}</span></p>}
              {lead.utm_campaign && <p><span style={{ color: 'var(--text-muted)' }}>Campaign:</span> <span style={{ color: 'var(--text-secondary)' }}>{lead.utm_campaign}</span></p>}
              {lead.referrer && <p><span style={{ color: 'var(--text-muted)' }}>Referrer:</span> <span style={{ color: 'var(--text-secondary)' }}>{lead.referrer}</span></p>}
              {lead.ip_address && <p><span style={{ color: 'var(--text-muted)' }}>IP:</span> <span style={{ color: 'var(--text-secondary)' }}>{lead.ip_address}</span></p>}
            </div>
          </div>

          {/* Audit Timeline */}
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Audit Timeline</h3>
            <div className="space-y-4">
              {audit.length === 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No audit entries</p>}
              {audit.map((entry, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full" style={{ background: 'var(--text-muted)' }} />
                    {i < audit.length - 1 && <div className="w-px flex-1 my-1" style={{ background: 'var(--border-separator)' }} />}
                  </div>
                  <div className="pb-2">
                    <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{entry.action.replace('.', ' ')}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')} by {entry.user_email}
                    </p>
                    {entry.before_data && entry.after_data && (
                      <p className="text-[11px] mt-1 font-mono rounded-lg px-2.5 py-1.5 border" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-separator)' }}>
                        {JSON.stringify(entry.before_data)} → {JSON.stringify(entry.after_data)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle — Workflow (35%) */}
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Workflow</h3>

            {/* Status */}
            <div className="mb-5">
              <label className="text-[10px] uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>Current Status</label>
              <div className="flex items-center gap-2">
                <BaseSelect
                  value={status || lead.status}
                  onChange={val => setStatus(val)}
                  options={(vertical?.lifecycle_statuses || ['new']).map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
                  className="flex-1"
                />
                <button
                  onClick={handleStatusSave}
                  disabled={!status || status === lead.status}
                  className="p-2.5 rounded-lg transition-all"
                  style={{
                    background: statusSaved ? 'var(--success-bg)' : 'var(--accent)',
                    color: statusSaved ? 'var(--success-text)' : 'var(--text-inverse)',
                    opacity: (!status || status === lead.status) ? 0.4 : 1,
                  }}
                >
                  {statusSaved ? <CheckCircle size={14} /> : <Save size={14} />}
                </button>
              </div>
            </div>

            {/* Routing */}
            <div className="mb-5">
              <label className="text-[10px] uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>Routing</label>
              <div className="text-sm space-y-2">
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-muted)' }}>Status:</span>
                  <span className="font-medium text-xs px-2 py-0.5 rounded-full border" style={lead.routing_status === 'assigned'
                    ? { background: 'var(--success-bg)', color: 'var(--success-text)', borderColor: 'var(--success-border)' }
                    : lead.routing_status === 'unmatched'
                      ? { background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }
                      : lead.routing_status === 'duplicate'
                        ? { background: 'var(--warning-bg)', color: 'var(--warning-text)', borderColor: 'var(--warning-border)' }
                        : { background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }
                  }>{lead.routing_status}</span>
                </div>
                {partner && (
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-muted)' }}>Partner:</span>
                    <span className="font-medium cursor-pointer transition-colors" style={{ color: 'var(--text-secondary)' }} onClick={() => navigate(`/partners/${partner.id}`)}>
                      {partner.display_name}
                    </span>
                  </div>
                )}
                {offer && (
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-muted)' }}>Offer:</span>
                    <span className="font-medium cursor-pointer transition-colors" style={{ color: 'var(--text-secondary)' }} onClick={() => navigate(`/offers/${offer.id}`)}>
                      {offer.payout_model.toUpperCase()} — {offer.payout_config.currency}
                    </span>
                  </div>
                )}
                {lead.routing_completed_at && (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Routed {formatDistanceToNow(new Date(lead.routing_completed_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>

            {/* Validation */}
            <div className="mb-5">
              <label className="text-[10px] uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>Validation</label>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border" style={lead.validation_status === 'valid'
                  ? { background: 'var(--success-bg)', color: 'var(--success-text)', borderColor: 'var(--success-border)' }
                  : lead.validation_status === 'invalid'
                    ? { background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }
                    : { background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }
                }>{lead.validation_status}</span>
                {lead.phone_verified !== null && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Phone {lead.phone_verified ? 'verified' : 'unverified'}</span>
                )}
                {lead.validation_status === 'pending' && (
                  <button
                    onClick={handleValidate}
                    disabled={validating}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-all disabled:opacity-50"
                    style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
                  >
                    {validating ? (
                      <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                    ) : (
                      <CheckCircle size={12} />
                    )}
                    Validate
                  </button>
                )}
              </div>
            </div>

            {/* Key dates */}
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-wider block mb-2" style={{ color: 'var(--text-muted)' }}>Key Dates</label>
              {[
                { label: 'Contacted', date: lead.contacted_at },
                { label: 'Quoted', date: lead.quoted_at },
                { label: 'Signed', date: lead.signed_at },
                { label: 'Completed', date: lead.completed_at },
              ].map(d => d.date && (
                <div key={d.label} className="flex items-center justify-between text-sm py-1" style={{ borderBottom: '1px solid var(--border-separator)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{d.label}</span>
                  <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>{format(new Date(d.date), 'MMM d, yyyy HH:mm')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* TCPA block */}
          {lead.tcpa_consent !== null && (
            <div className="rounded-xl border p-5" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
              <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                <Shield size={14} style={{ color: 'var(--text-muted)' }} />
                TCPA Consent
              </h3>
              <div className="space-y-3 text-sm">
                <p>
                  <span style={{ color: 'var(--text-muted)' }}>Status:</span>{' '}
                  <span className="font-medium" style={{ color: lead.tcpa_consent ? 'var(--success-text)' : 'var(--danger-text)' }}>
                    {lead.tcpa_consent ? 'Consented' : 'Not consented'}
                  </span>
                </p>
                {lead.tcpa_consent_text && (
                  <div>
                    <p style={{ color: 'var(--text-muted)' }}>Consent text:</p>
                    <p className="p-3 rounded-lg text-xs italic border mt-1" style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)' }}>
                      &ldquo;{lead.tcpa_consent_text}&rdquo;
                    </p>
                  </div>
                )}
                {lead.tcpa_consent_at && (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Recorded {format(new Date(lead.tcpa_consent_at), 'MMM d, yyyy HH:mm:ss')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right — Intake + Notes (30%) */}
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Intake Data</h3>
            <div className="space-y-4">
              {renderIntakeData(lead.intake_data)}
            </div>
          </div>

          {/* Operator notes */}
          <div className="rounded-xl border p-5 card-shadow" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Operator Notes</h3>
            <textarea
              value={operatorNotes || lead.operator_notes || ''}
              onChange={e => setOperatorNotes(e.target.value)}
              placeholder="Internal notes — never visible to partners"
              rows={4}
              maxLength={500}
              className="w-full text-sm rounded-lg px-3 py-2.5 outline-none resize-none transition-all"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {(operatorNotes || lead.operator_notes || '').length}/500
              </span>
              <button
                onClick={handleSaveNotes}
                className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg transition-all"
                style={{ background: notesSaved ? 'var(--success-bg)' : 'var(--accent)', color: notesSaved ? 'var(--success-text)' : 'var(--text-inverse)' }}
              >
                {notesSaved ? <CheckCircle size={12} /> : <Save size={12} />}
                {notesSaved ? 'Saved' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}